import { XMarkIcon } from '@heroicons/react/24/solid'
import Loader from 'components/Loader'
import { useEffect, useRef, type JSX } from 'react'
import ModalHeader from '../ModalHeader'

interface ModalProps {
    children: JSX.Element
    title: string
    desc?: string
    open: boolean
    onClose: () => void
    loading?: boolean
}

export const HtmlDialogModal = ({ children, title, desc, open: isOpen, onClose, loading }: ModalProps) => {
    const dialogRef = useRef<HTMLDialogElement>(null)

    useEffect(() => {
        if (!dialogRef.current) {
            return
        }

        if (isOpen) {
            dialogRef.current.showModal()
        } else {
            dialogRef.current.close()
        }
    }, [isOpen])

    // If the modal gets closed by using the escape key, make sure to also call the external onClose function to sync up the React state
    function handleOnClose() {
        onClose()
    }

    const handleBackdropClick: React.MouseEventHandler<HTMLDialogElement> = (e) => {
        if (e.target === dialogRef.current) {
            onClose()
        }
    }

    return (
        <dialog
            ref={dialogRef}
            className='backdrop:bg-black/50 w-full sm:w-auto sm:max-w-xl sm:min-w-120 max-h-[97%] rounded-xl'
            onClick={handleBackdropClick}
            onClose={handleOnClose}
        >
            <div>
                <ModalHeader title={title} desc={desc} />
                {loading ? (
                    <div className='w-full h-40 flex justify-center items-center'>
                        <Loader />
                    </div>
                ) : (
                    children
                )}
                <button className='absolute top-4 right-4' type='button' onClick={onClose}>
                    <XMarkIcon className='text-black w-6' />
                </button>
            </div>
        </dialog>
    )
}
